/**
 * Phase 27.50.B.10 — Deletion audit log page (KR admin only).
 *
 * Lists every request/order that has been hard-deleted along with:
 *   • Who deleted it, when
 *   • A short summary line
 *   • Whether it has already been restored
 *   • A one-click Restore action (if not yet restored)
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, History, RotateCcw, CheckCircle2, FileX,
} from 'lucide-react';
import { deletionAuditAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const RESOURCE_LABELS = {
  marketing_request: 'Marketing',
  event_request: 'Event',
  retail_request: 'Retail',
};

const RESOURCE_COLORS = {
  marketing_request: 'bg-blue-100 text-blue-800 border-blue-300',
  event_request: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  retail_request: 'bg-amber-100 text-amber-800 border-amber-300',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminDeletionLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [resourceFilter, setResourceFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (resourceFilter !== 'all') params.resource = resourceFilter;
      const r = await deletionAuditAPI.list(params);
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load deletion log'));
    } finally { setLoading(false); }
  }, [resourceFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (row) => {
    if (!window.confirm(`Restore this ${RESOURCE_LABELS[row.resource_type] || 'request'}?\n\n${row.summary}`)) return;
    setRestoringId(row.audit_id);
    try {
      await deletionAuditAPI.restore(row.audit_id);
      toast.success('Restored');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Restore failed'));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="deletion-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <History size={22} /> Deletion Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every request or order that has been permanently deleted, with a one-click Restore.
          </p>
        </div>
        <div className="w-56">
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger data-testid="filter-resource">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              <SelectItem value="marketing_request">Marketing requests</SelectItem>
              <SelectItem value="event_request">Event requests</SelectItem>
              <SelectItem value="retail_request">Retail requests</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-slate-500">
            <FileX className="mx-auto mb-3 text-slate-300" size={32} />
            <p>No deletions on record.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3 font-semibold">Deleted</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Resource</th>
                  <th className="text-left p-3 font-semibold">Deleted by</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.audit_id} data-testid={`log-row-${r.audit_id}`}>
                    <td className="p-3 text-slate-600 whitespace-nowrap">{formatDateTime(r.deleted_at)}</td>
                    <td className="p-3">
                      <Badge
                        variant="outline"
                        className={RESOURCE_COLORS[r.resource_type] || ''}
                      >
                        {RESOURCE_LABELS[r.resource_type] || r.resource_type}
                      </Badge>
                    </td>
                    <td className="p-3 text-slate-800">
                      <div className="font-medium">{r.summary || '—'}</div>
                      <div className="text-xs text-slate-400 font-mono">
                        {(r.resource_id || '').slice(0, 8)}
                      </div>
                      {(r.deletion_reason || r.deletion_reason_preset) && (
                        <div className="mt-1 text-xs text-slate-600 italic border-l-2 border-slate-200 pl-2">
                          {r.deletion_reason || r.deletion_reason_preset}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      <div className="text-slate-800">{r.deleted_by_name}</div>
                      <div className="text-xs text-slate-400">{r.deleted_by_email}</div>
                    </td>
                    <td className="p-3">
                      {r.restored_at ? (
                        <div className="flex items-center gap-1.5 text-emerald-700">
                          <CheckCircle2 size={14} />
                          <span className="text-xs">
                            Restored {formatDateTime(r.restored_at)}
                          </span>
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">
                          Deleted
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {!r.restored_at && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRestore(r)}
                          disabled={restoringId === r.audit_id}
                          data-testid={`restore-${r.audit_id}`}
                        >
                          {restoringId === r.audit_id ? (
                            <Loader2 size={14} className="mr-1.5 animate-spin" />
                          ) : (
                            <RotateCcw size={14} className="mr-1.5" />
                          )}
                          Restore
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
